import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { applicationsAPI } from '../services/api';
import { validateEmail, validatePhone, validateParentId } from '../utils/helpers';

const CLASS_OPTIONS = [
  'Senior 1', 'Senior 4 MS I', 'Senior 4 MS II', 'Senior 4 ARH', 'TVET LEVEL 3 SOD', 'Associate Nursing Program'
];

const PREVIOUS_CLASS_OPTIONS = ['Primary 6', 'Senior 3'];

const FEE_MAP = {
  'Senior 1': '1,200,000',
  'Senior 4 MS I': '1,400,000',
  'Senior 4 MS II': '1,400,000',
  'Senior 4 ARH': '1,400,000',
  'TVET LEVEL 3 SOD': '1,400,000',
  'Associate Nursing Program': '1,400,000'
};

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('email');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [locations, setLocations] = useState(null);

  const [formData, setFormData] = useState({
    applicantEmail: '',
    lastName: '',
    firstName: '',
    gender: '',
    birthDate: '',
    religion: '',
    nationality: '',
    classAppliedFor: '',
    province: '',
    district: '',
    sector: '',
    cell: '',
    village: '',
    fatherNames: '',
    fatherEmail: '',
    fatherID: '',
    fatherPhoneNumber: '',
    motherNames: '',
    motherEmail: '',
    motherID: '',
    motherPhoneNumber: '',
    previousAcademicYear: '2025-2026',
    previousClass: '',
    previousSchool: '',
    annualPreviousMarks: '',
    siblingsAtSchool: 'No',
    siblingsList: '',
    comment: '',
    insuranceName: '',
    parentOccupation: '',
    willingToPayFull: 'No',
    guardianNames: '',
    guardianEmail: '',
    guardianPhoneNumber: '',
    guardianComment: '',
    sdmsCode: '',
    nationalExamIndex: '',
    internationalCode: ''
  });

  const [reportCard, setReportCard] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [dataConsent, setDataConsent] = useState(false);
  const [willingToPayConsent, setWillingToPayConsent] = useState(false);
  const [marksError, setMarksError] = useState('');
  const [studentIdType, setStudentIdType] = useState('');
  const [bornDateError, setBornDateError] = useState('');
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetch('/geographical_location.json')
      .then(res => res.json())
      .then(data => setLocations(data.items))
      .catch(() => setLocations([]));
    fetch('/api/settings/deadline')
      .then(res => res.json())
      .then(data => {
        if (data.deadline) {
          const deadlineDate = new Date(data.deadline);
          if (new Date() > deadlineDate) setDeadlinePassed(true);
        }
        if (data.startDate) {
          const start = new Date(data.startDate);
          if (new Date() < start) setStartDate(start);
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'province') {
        updated.district = '';
        updated.sector = '';
        updated.cell = '';
        updated.village = '';
      } else if (name === 'district') {
        updated.sector = '';
        updated.cell = '';
        updated.village = '';
      } else if (name === 'sector') {
        updated.cell = '';
        updated.village = '';
      } else if (name === 'cell') {
        updated.village = '';
      } else if (name === 'classAppliedFor') {
        if (value === 'Senior 1') {
          updated.previousClass = 'Primary 6';
        } else {
          updated.previousClass = 'Senior 3';
        }
      }
      return updated;
    });
    setError('');
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const digits = value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: digits
    }));
    setError('');
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const getProvinces = () => locations || [];
  const getDistricts = () => {
    const province = getProvinces().find(p => p.name === formData.province);
    return province?.districts || [];
  };
  const getSectors = () => {
    const district = getDistricts().find(d => d.name === formData.district);
    return district?.sectors || [];
  };
  const getCells = () => {
    const sector = getSectors().find(s => s.name === formData.sector);
    return sector?.cells || [];
  };
  const getVillages = () => {
    const cell = getCells().find(c => c.name === formData.cell);
    return cell?.villages || [];
  };

  const validateParentFields = () => {
    const errors = {};

    if (!validateEmail(formData.fatherEmail)) {
      errors.fatherEmail = 'Invalid email format';
    }
    if (!validateEmail(formData.motherEmail)) {
      errors.motherEmail = 'Invalid email format';
    }

    if (!validatePhone(formData.fatherPhoneNumber)) {
      errors.fatherPhoneNumber = 'Must be 12 digits starting with 250 (e.g. 25078xxxxxxx)';
    }
    if (!validatePhone(formData.motherPhoneNumber)) {
      errors.motherPhoneNumber = 'Must be 12 digits starting with 250 (e.g. 25078xxxxxxx)';
    }

    if (!validateParentId(formData.fatherID)) {
      errors.fatherID = 'Must be exactly 16 digits';
    }
    if (!validateParentId(formData.motherID)) {
      errors.motherID = 'Must be exactly 16 digits';
    }

    if (formData.parentOccupation && formData.parentOccupation.length > 100) {
      errors.parentOccupation = 'Must be 100 characters or less';
    }

    if (formData.guardianEmail && !validateEmail(formData.guardianEmail)) {
      errors.guardianEmail = 'Invalid email format';
    }
    if (formData.guardianPhoneNumber) {
      const guardianDigits = formData.guardianPhoneNumber.replace(/\D/g, '');
      if (guardianDigits && !validatePhone(guardianDigits)) {
        errors.guardianPhoneNumber = 'Must be 12 digits starting with 250 (e.g. 25078xxxxxxx)';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailVerification = async (e) => {
    e.preventDefault();
    if (!formData.applicantEmail) {
      setError('Please enter your email address');
      return;
    }

    setEmailChecking(true);
    setError('');
    setVerificationMessage('');

    if (startDate && new Date() < startDate) {
      setError(`Applications open on ${startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}. Please check back then.`);
      setEmailChecking(false);
      return;
    }

    try {
      await axios.post('/api/applications/send-verification-code', { email: formData.applicantEmail });
      setCodeSent(true);
      setVerificationMessage('✓ A verification code has been sent to your email. Please check your inbox.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending verification code');
      setCodeSent(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setCodeVerifying(true);
    setError('');
    setVerificationMessage('');

    try {
      await axios.post('/api/applications/verify-code', { email: formData.applicantEmail, code: verificationCode });
      setEmailVerified(true);
      setVerificationMessage('✓ Email verified successfully!');
      setCurrentStep('student');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setCodeVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const MAX_SIZE = 2 * 1024 * 1024;
    if (reportCard) {
      for (const f of reportCard) {
        if (f.size > MAX_SIZE) {
          setError(`Report card file "${f.name}" exceeds the 2MB limit`);
          setLoading(false);
          return;
        }
      }
    }
    if (paymentProof && paymentProof.size > MAX_SIZE) {
      setError(`Payment proof file "${paymentProof.name}" exceeds the 2MB limit`);
      setLoading(false);
      return;
    }

    if (!willingToPayConsent) {
      setError('You must commit to pay 100% of school fees to continue');
      setLoading(false);
      return;
    }
    if (!dataConsent) {
      setError('You must agree to the data protection policy');
      setLoading(false);
      return;
    }
    if (!reportCard) {
      setError('Please upload the previous academic year report card');
      setLoading(false);
      return;
    }
    if (!paymentProof) {
      setError('Please upload the proof of application fee payment (100,000Rwf)');
      setLoading(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ ...formData, dataConsent: '1', willingToPayFull: willingToPayConsent ? 'Yes' : 'No' }));
      for (let i = 0; i < reportCard.length; i++) fd.append('reportCard', reportCard[i]);
      fd.append('paymentProof', paymentProof);

      const res = await applicationsAPI.submitApplication(fd);
      const appNumber = res.data?.applicationNumber || '';

      setMessage(`Application submitted successfully! Your application number is ${appNumber}.`);
      setCurrentStep('submitted');

      setTimeout(() => {
        navigate('/');
      }, 120000);
    } catch (err) {
      const data = err.response?.data;
      if (data?.field) {
        setFieldErrors({ [data.field]: data.message });
      }
      setError(data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <img src="/school.png" alt="Hope Haven Christian School" className="h-24 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-1">Hope Haven Christian School</h1>
          <p className="text-lg sm:text-xl font-semibold text-blue-600 mb-1">Student Application Form</p>
          <p className="text-sm sm:text-base text-gray-600">Join our school community</p>
        </div>

        {/* Progress Steps */}
        {emailVerified && (
          <div className="mb-6 sm:mb-8">
            <div className="flex justify-between mb-4 text-xs sm:text-sm">
              {['Student Info', 'Parents Info', 'Academic Info', 'Additional'].map((label, i) => {
                const steps = ['student', 'parents', 'academic', 'additional'];
                return (
                  <div key={label} className={`flex-1 text-center pb-2 sm:pb-4 ${currentStep === steps[i] ? 'border-b-4 border-blue-600' : 'border-b-2 border-gray-300'}`}>
                    <p className={`font-semibold ${currentStep === steps[i] ? 'text-blue-600' : 'text-gray-600'}`}>{label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div className="mb-4 sm:mb-6 bg-green-50 border-l-4 border-green-600 p-3 sm:p-4 rounded">
            <p className="text-green-800 font-semibold text-sm sm:text-base">{message}</p>
          </div>
        )}
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-600 p-3 sm:p-4 rounded">
            <p className="text-red-800 font-semibold text-sm sm:text-base">{error}</p>
          </div>
        )}
        {verificationMessage && (
          <div className="mb-4 sm:mb-6 bg-green-50 border-l-4 border-green-600 p-3 sm:p-4 rounded">
            <p className="text-green-800 text-sm sm:text-base">{verificationMessage}</p>
          </div>
        )}

        {/* Email Verification Step */}
        {!emailVerified && (
          <>
            {!codeSent ? (
              <form onSubmit={handleEmailVerification} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Step 1: Email Verification</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">First, let's verify your email address. We'll send a verification code to your email.</p>

                  {startDate && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-600 p-3 sm:p-4 rounded mb-4">
                      <p className="text-sm text-yellow-800 font-semibold">Applications open on {startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}. You cannot submit before this date.</p>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Your Email Address *</label>
                    <input
                      type="email"
                      name="applicantEmail"
                      value={formData.applicantEmail}
                      onChange={handleChange}
                      disabled={emailChecking}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 disabled:bg-gray-100 text-sm sm:text-base"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-600 p-3 sm:p-4 rounded mb-4 sm:mb-6">
                    <p className="text-gray-700 text-xs sm:text-sm">
                      <strong>Important:</strong> You can submit only one application per email address. Make sure you use the correct email as you'll receive confirmation and updates here.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={emailChecking}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {emailChecking ? 'Sending Code...' : 'Send Verification Code'}
                  </button>

                  <p className="text-center mt-4 sm:mt-6">
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-sm sm:text-base"
                    >
                      Back to Information
                    </button>
                  </p>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Step 1: Enter Verification Code</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-4">A 6-digit code was sent to <strong>{formData.applicantEmail}</strong>. Please enter it below.</p>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Verification Code *</label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base text-center text-2xl tracking-widest"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>

                  <p className="text-xs text-gray-500 mb-4">The code expires in 10 minutes. Check your spam folder if you don't see it.</p>

                  <button
                    type="submit"
                    disabled={codeVerifying || verificationCode.length !== 6}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {codeVerifying ? 'Verifying...' : 'Verify Code & Continue'}
                  </button>

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setCodeSent(false)}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg transition duration-200 text-sm"
                    >
                      Change Email
                    </button>
                    <button
                      type="button"
                      onClick={handleEmailVerification}
                      disabled={emailChecking}
                      className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 font-bold py-2 px-4 rounded-lg transition duration-200 text-sm disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>
              </form>
            )}
          </>
        )}

        {/* Student Information Step */}
        {emailVerified && currentStep === 'student' && (
          <form onSubmit={(e) => {
            e.preventDefault();
            // Validate age >= 10
            if (formData.birthDate) {
              const birth = new Date(formData.birthDate);
              const today = new Date();
              let age = today.getFullYear() - birth.getFullYear();
              const m = today.getMonth() - birth.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
              if (age < 10) {
                setBornDateError('Student must be at least 10 years old');
                return;
              }
            }
            setBornDateError('');
            setCurrentStep('parents');
          }} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Student Information</h2>
            <p className="text-sm text-gray-500 mb-4">Note: Student must be at least 10 years old and must have scored 80% or above in previous academic year.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Gender *</label>
                <select name="gender" value={formData.gender} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Birth Date *</label>
                <input type="date" name="birthDate" value={formData.birthDate} onChange={(e) => {
                  handleChange(e);
                  setBornDateError('');
                }} required className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${bornDateError ? 'border-red-500' : 'border-gray-300'}`} />
                {bornDateError && <p className="text-red-600 text-xs sm:text-sm mt-1">{bornDateError}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Religion *</label>
                <input type="text" name="religion" value={formData.religion} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Nationality *</label>
                <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Class Applied For *</label>
              <select name="classAppliedFor" value={formData.classAppliedFor} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                <option value="">Select Class</option>
                {CLASS_OPTIONS.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
              {formData.classAppliedFor && FEE_MAP[formData.classAppliedFor] && (
                <p className="text-blue-700 text-sm mt-2 font-semibold">
                  School Fees (per term): {FEE_MAP[formData.classAppliedFor]} Rwf
                </p>
              )}
            </div>

            {/* Address Section */}
            <div className="border-t-2 border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Address Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Province *</label>
                  <select name="province" value={formData.province} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                    <option value="">Select Province</option>
                    {getProvinces().map(p => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">District *</label>
                  <select name="district" value={formData.district} onChange={handleChange} required disabled={!formData.province} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <option value="">Select District</option>
                    {getDistricts().map(d => (
                      <option key={d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Sector *</label>
                  <select name="sector" value={formData.sector} onChange={handleChange} required disabled={!formData.district} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <option value="">Select Sector</option>
                    {getSectors().map(s => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Cell *</label>
                  <select name="cell" value={formData.cell} onChange={handleChange} required disabled={!formData.sector} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <option value="">Select Cell</option>
                    {getCells().map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Village *</label>
                  <select name="village" value={formData.village} onChange={handleChange} required disabled={!formData.cell} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <option value="">Select Village</option>
                    {getVillages().map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep('email');
                  setEmailVerified(false);
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Next: Parents Information
              </button>
            </div>
          </form>
        )}

        {/* Parents Information Step */}
        {emailVerified && currentStep === 'parents' && (
          <form onSubmit={(e) => { e.preventDefault(); if (validateParentFields()) setCurrentStep('academic'); }} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Parents Information</h2>

            <div className="border-b-2 border-gray-200 pb-4 sm:pb-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Father Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Father Names *</label>
                  <input type="text" name="fatherNames" value={formData.fatherNames} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Father Email *</label>
                  <input type="email" name="fatherEmail" value={formData.fatherEmail} onChange={handleChange} required className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.fatherEmail ? 'border-red-500' : 'border-gray-300'}`} />
                  {fieldErrors.fatherEmail && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.fatherEmail}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Father ID (16 digits) *</label>
                  <input type="text" name="fatherID" value={formData.fatherID} onChange={handleChange} required maxLength={16} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.fatherID ? 'border-red-500' : 'border-gray-300'}`} />
                  {fieldErrors.fatherID && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.fatherID}</p>}
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Father Phone (250xxxxxxxxx) *</label>
                  <input type="tel" name="fatherPhoneNumber" value={formData.fatherPhoneNumber} onChange={handlePhoneChange} required maxLength={12} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.fatherPhoneNumber ? 'border-red-500' : 'border-gray-300'}`} placeholder="25078xxxxxxx" />
                  {fieldErrors.fatherPhoneNumber && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.fatherPhoneNumber}</p>}
                </div>
              </div>
            </div>

            <div className="pb-4 sm:pb-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Mother Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Mother Names *</label>
                  <input type="text" name="motherNames" value={formData.motherNames} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Mother Email *</label>
                  <input type="email" name="motherEmail" value={formData.motherEmail} onChange={handleChange} required className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.motherEmail ? 'border-red-500' : 'border-gray-300'}`} />
                  {fieldErrors.motherEmail && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.motherEmail}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Mother ID (16 digits) *</label>
                  <input type="text" name="motherID" value={formData.motherID} onChange={handleChange} required maxLength={16} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.motherID ? 'border-red-500' : 'border-gray-300'}`} />
                  {fieldErrors.motherID && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.motherID}</p>}
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Mother Phone (250xxxxxxxxx) *</label>
                  <input type="tel" name="motherPhoneNumber" value={formData.motherPhoneNumber} onChange={handlePhoneChange} required maxLength={12} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.motherPhoneNumber ? 'border-red-500' : 'border-gray-300'}`} placeholder="25078xxxxxxx" />
                  {fieldErrors.motherPhoneNumber && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.motherPhoneNumber}</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => setCurrentStep('student')}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Next: Academic Information
              </button>
            </div>
          </form>
        )}

        {/* Academic Information Step */}
        {emailVerified && currentStep === 'academic' && (
          <form onSubmit={(e) => {
            e.preventDefault();
            const marks = parseInt(formData.annualPreviousMarks);
            if (isNaN(marks) || marks < 80) {
              setMarksError('Previous academic year marks must be 80% or above. Applications with marks below 80% will not be accepted.');
              return;
            }
            setMarksError('');
            if (!studentIdType) {
              setError('Please select a student identification method');
              return;
            }
            if (studentIdType === 'sdms' && formData.sdmsCode.length !== 12) {
              setError('SDMS Code must be exactly 12 digits');
              return;
            }
            if (studentIdType === 'examIndex') {
              const isS4 = ['Senior 4 MS I', 'Senior 4 MS II', 'Senior 4 ARH', 'TVET LEVEL 3 SOD', 'Associate Nursing Program'].includes(formData.classAppliedFor);
              const pattern = formData.classAppliedFor === 'Senior 1' ? /^\d{6}PR\d{3}\d{4}$/ : /^\d{6}OL\d{3}\d{4}$/;
              if (!pattern.test(formData.nationalExamIndex)) {
                setError(`Invalid National Exam Index format. ${isS4 ? 'Expected format: ######OL###2026' : 'Expected format: ######PR###2026'}`);
                return;
              }
            }
            if (studentIdType === 'international' && formData.internationalCode.length !== 10) {
              setError('International Registration Number must be exactly 10 digits');
              return;
            }
            setError('');
            setCurrentStep('additional');
          }} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Academic Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Previous Academic Year</label>
                <select name="previousAcademicYear" value={formData.previousAcademicYear} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                  <option value="2025-2026">2025-2026</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Previous Class *</label>
                <select name="previousClass" value={formData.previousClass} disabled className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg bg-gray-100 text-sm sm:text-base cursor-not-allowed">
                  <option value="">Select Previous Class</option>
                  {PREVIOUS_CLASS_OPTIONS.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">Auto-filled based on class applied for</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Previous School *</label>
                <input type="text" name="previousSchool" value={formData.previousSchool} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Annual Previous Marks (%) *</label>
                <input type="number" name="annualPreviousMarks" value={formData.annualPreviousMarks} onChange={(e) => {
                  setFormData(prev => ({ ...prev, annualPreviousMarks: e.target.value }));
                  setMarksError('');
                }} min="0" max="100" required className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${marksError ? 'border-red-500' : 'border-gray-300'}`} />
                {marksError && <p className="text-red-600 text-xs sm:text-sm mt-1">{marksError}</p>}
                <p className="text-gray-500 text-xs mt-1">Minimum required: 80%</p>
              </div>
            </div>

            {/* SDMS Code or National Exam Index */}
            <div className="border-t-2 border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Student Identification *</h3>
              <p className="text-sm text-gray-500 mb-3">Select one identification method and enter the corresponding number.</p>
              <div className="flex gap-6 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="studentIdType" value="sdms" checked={studentIdType === 'sdms'} onChange={(e) => { setStudentIdType(e.target.value); setFormData(prev => ({ ...prev, sdmsCode: '', nationalExamIndex: '', internationalCode: '' })); }} className="h-4 w-4 text-blue-600" />
                  <span className="text-sm sm:text-base font-semibold text-gray-700">SDMS Student Code</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="studentIdType" value="examIndex" checked={studentIdType === 'examIndex'} onChange={(e) => { setStudentIdType(e.target.value); setFormData(prev => ({ ...prev, sdmsCode: '', nationalExamIndex: '', internationalCode: '' })); }} className="h-4 w-4 text-blue-600" />
                  <span className="text-sm sm:text-base font-semibold text-gray-700">National Exam Index Number</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="studentIdType" value="international" checked={studentIdType === 'international'} onChange={(e) => { setStudentIdType(e.target.value); setFormData(prev => ({ ...prev, sdmsCode: '', nationalExamIndex: '', internationalCode: '' })); }} className="h-4 w-4 text-blue-600" />
                  <span className="text-sm sm:text-base font-semibold text-gray-700">International Registration Number (Cambridge)</span>
                </label>
              </div>
              {studentIdType === 'international' && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">International Registration Number (10 digits) *</label>
                  <input type="text" name="internationalCode" value={formData.internationalCode} onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, internationalCode: val }));
                  }} placeholder="1234567890" maxLength={10} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                </div>
              )}
              {studentIdType === 'sdms' && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">SDMS Code (12 digits) *</label>
                  <input type="text" name="sdmsCode" value={formData.sdmsCode} onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                    setFormData(prev => ({ ...prev, sdmsCode: val }));
                  }} placeholder="123456789012" maxLength={12} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                </div>
              )}
              {studentIdType === 'examIndex' && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">National Examination Index Number *</label>
                  {formData.classAppliedFor === 'Senior 1' && (
                    <>
                      <p className="text-xs text-gray-500 mb-1">Format: ######PR###2026 (6 digits, PR, 3 digits, 4-digit year)</p>
                      <input type="text" name="nationalExamIndex" value={formData.nationalExamIndex} onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^0-9PR]/g, '').slice(0, 16);
                        setFormData(prev => ({ ...prev, nationalExamIndex: val }));
                      }} placeholder="123456PR7892026" maxLength={16} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                    </>
                  )}
                  {['Senior 4 MS I', 'Senior 4 MS II', 'Senior 4 ARH', 'TVET LEVEL 3 SOD', 'Associate Nursing Program'].includes(formData.classAppliedFor) && (
                    <>
                      <p className="text-xs text-gray-500 mb-1">Format: ######OL###2026 (6 digits, OL, 3 digits, 4-digit year)</p>
                      <input type="text" name="nationalExamIndex" value={formData.nationalExamIndex} onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^0-9OL]/g, '').slice(0, 16);
                        setFormData(prev => ({ ...prev, nationalExamIndex: val }));
                      }} placeholder="123456OL7892026" maxLength={16} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => setCurrentStep('parents')}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Next: Additional Information
              </button>
            </div>
          </form>
        )}

        {/* Additional Information Step */}
        {emailVerified && currentStep === 'additional' && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Additional Information</h2>

            <div className="mb-4 sm:mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Do you have any siblings at Hope Haven Christian School? *</label>
              <select name="siblingsAtSchool" value={formData.siblingsAtSchool} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            {formData.siblingsAtSchool === 'Yes' && (
              <div className="mb-4 sm:mb-6">
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">If Yes, List Them</label>
                <textarea name="siblingsList" value={formData.siblingsList} onChange={handleChange} rows="3" placeholder="List siblings and their classes" className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base"></textarea>
              </div>
            )}

            <div className="mb-4 sm:mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Comment</label>
              <textarea name="comment" value={formData.comment} onChange={handleChange} rows="3" placeholder="Any additional comments" className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base"></textarea>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Insurance Name</label>
                <input type="text" name="insuranceName" value={formData.insuranceName} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Parent Occupation</label>
                <input type="text" name="parentOccupation" value={formData.parentOccupation} onChange={handleChange} maxLength={100} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.parentOccupation ? 'border-red-500' : 'border-gray-300'}`} />
                {fieldErrors.parentOccupation && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.parentOccupation}</p>}
              </div>
            </div>

            {/* Willing to Pay Full Fees Consent */}
            <div className="mb-4 sm:mb-6 bg-blue-50 border border-blue-300 p-3 sm:p-4 rounded">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={willingToPayConsent} onChange={(e) => setWillingToPayConsent(e.target.checked)} className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm sm:text-base text-gray-700">
                  <strong>Parent Commitment to Pay 100% School Fees</strong><br />
                  I confirm and commit that if my child excels in both the entry exam and national exam, I will pay 100% of the school fees{formData.classAppliedFor && FEE_MAP[formData.classAppliedFor] ? ` (${FEE_MAP[formData.classAppliedFor]} Rwf per term)` : ''}. This is a mandatory requirement for application.
                </span>
              </label>
            </div>

            {/* Application Fee Notice */}
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-3 sm:p-4 rounded mb-4 sm:mb-6">
              <p className="text-yellow-800 font-bold text-sm sm:text-base">Application Fee: 100,000 Rwf</p>
              <p className="text-yellow-700 text-xs sm:text-sm mt-1">Please make payment to <strong>Account #100145699155 — HOPE HAVEN LTD</strong> in BANK OF KIGALI and upload the proof below.</p>
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">
                  {formData.classAppliedFor === 'Senior 1'
                    ? (studentIdType === 'international' ? 'Upload Grade 6 or Year 7 Report Cards *' : 'Upload Primary 5 & Primary 6 Term 1 & 2 Report Cards *')
                    : ['Senior 4 MS I', 'Senior 4 MS II', 'Senior 4 ARH'].includes(formData.classAppliedFor)
                    ? (studentIdType === 'international' ? 'Upload Grade 10 or Year 11 Report Cards *' : 'Upload S1, S2 & S3 Grade Reports *')
                    : 'Previous Year Report Card *'}
                </label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setReportCard(e.target.files)} multiple required className="w-full text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                {reportCard && reportCard.length > 0 && (
                  <div className="mt-1">
                    {Array.from(reportCard).map((f, i) => (
                      <p key={i} className="text-green-600 text-xs">✓ {f.name}</p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Proof of Payment (100,000 Rwf) *</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPaymentProof(e.target.files[0])} required className="w-full text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                {paymentProof && <p className="text-green-600 text-xs mt-1">✓ {paymentProof.name}</p>}
              </div>
            </div>

            {/* Deadline Passed Warning */}
            {deadlinePassed && (
              <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-600 p-3 sm:p-4 rounded">
                <p className="text-red-800 font-semibold text-sm sm:text-base">The application deadline has passed. No new submissions are accepted.</p>
              </div>
            )}

            <div className="border-t-2 border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Guardian Information (If Applicable)</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Guardian Names</label>
                  <input type="text" name="guardianNames" value={formData.guardianNames} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Guardian Email</label>
                  <input type="email" name="guardianEmail" value={formData.guardianEmail} onChange={handleChange} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.guardianEmail ? 'border-red-500' : 'border-gray-300'}`} />
                  {fieldErrors.guardianEmail && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.guardianEmail}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Guardian Phone (250xxxxxxxxx)</label>
                  <input type="tel" name="guardianPhoneNumber" value={formData.guardianPhoneNumber} onChange={handlePhoneChange} maxLength={12} className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.guardianPhoneNumber ? 'border-red-500' : 'border-gray-300'}`} placeholder="25078xxxxxxx" />
                  {fieldErrors.guardianPhoneNumber && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.guardianPhoneNumber}</p>}
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Guardian Comment</label>
                <textarea name="guardianComment" value={formData.guardianComment} onChange={handleChange} rows="3" className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base"></textarea>
              </div>
            </div>

            {/* Data Protection Consent */}
            <div className="mb-4 sm:mb-6 bg-gray-50 border border-gray-300 p-3 sm:p-4 rounded">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={dataConsent} onChange={(e) => setDataConsent(e.target.checked)} className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm sm:text-base text-gray-700">
                  I consent to the collection and processing of my personal data in accordance with the data protection policy of Hope Haven Christian School. I understand that my information will be used solely for application processing and admissions purposes.
                </span>
              </label>
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => setCurrentStep('academic')}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || deadlinePassed}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}

        {currentStep === 'submitted' && (
          <div className="text-center py-8 sm:py-16 px-4">
            <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-10">
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
              <p className="text-lg sm:text-xl text-gray-700 mb-4">
                Your application has been submitted successfully.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-gray-700 text-sm sm:text-base mb-2">
                  <strong>What happens next?</strong>
                </p>
                <ol className="list-decimal list-inside text-gray-600 text-sm sm:text-base space-y-1">
                  <li>Check your email for a confirmation message with your application number</li>
                  <li>Our admissions team will review your application and documents</li>
                  <li>You will be notified of the admission decision via email</li>
                  <li>Keep an eye on your email inbox (and spam folder) for updates</li>
                </ol>
              </div>
              <p className="text-gray-500 text-sm">
                You will be redirected to the home page in 2 minutes...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationForm;
